import { useState, useEffect, useCallback } from "react";
import {
  Flame, Users, ChevronRight, X, CheckCircle, AlertCircle,
  Settings, Edit3, TrendingUp, Apple
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MACRO_CONFIG = [
  { key: "calorieen",    label: "Calorieën", unit: "kcal", color: "#C9A84C" },
  { key: "eiwitten",     label: "Eiwitten",  unit: "g",    color: "#60a5fa" },
  { key: "koolhydraten", label: "Koolhyd.",  unit: "g",    color: "#34d399" },
  { key: "vetten",       label: "Vetten",    unit: "g",    color: "#f97316" },
];

function MiniProgress({ huidig, doel, color }) {
  const pct = doel > 0 ? Math.min((huidig / doel) * 100, 100) : 0;
  const over = doel > 0 && huidig > doel;
  return (
    <div className="h-1 bg-white/5 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: over ? "#ef4444" : color }} />
    </div>
  );
}

function SporterVoedingModal({ sporter, token, onClose }) {
  const [datum, setDatum] = useState(new Date().toISOString().split("T")[0]);
  const [logData, setLogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDoelen, setShowDoelen] = useState(false);
  const [doelForm, setDoelForm] = useState(null);
  const [savingDoelen, setSavingDoelen] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async (d = datum) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/nutrition/sporters/${sporter.id}/log?datum=${d}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) {
        const data = await r.json();
        setLogData(data);
        if (!doelForm) setDoelForm(data.doelen);
      }
    } catch {}
    setLoading(false);
  }, [sporter.id, token]); // eslint-disable-line

  useEffect(() => { load(datum); }, [datum]); // eslint-disable-line

  const saveDoelen = async () => {
    setSavingDoelen(true);
    try {
      const r = await fetch(`${API}/api/nutrition/sporters/${sporter.id}/doelen`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(doelForm),
      });
      if (!r.ok) throw new Error("Opslaan mislukt");
      setToast({ msg: "Doelen opgeslagen!", type: "success" });
      setTimeout(() => setToast(null), 3000);
      setShowDoelen(false);
      load(datum);
    } catch (e) { setToast({ msg: e.message, type: "error" }); setTimeout(() => setToast(null), 3000); }
    setSavingDoelen(false);
  };

  const inputCls = "bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 w-full";
  const { totalen = {}, doelen = {}, maaltijden = [], notitie = "" } = logData || {};

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/8 shrink-0">
          <div>
            <h3 className="text-white font-bold">{sporter.name}</h3>
            <p className="text-zinc-500 text-xs mt-0.5">Voedingslog</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowDoelen(v => !v)}
              data-testid="toggle-doelen-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-white/10 text-zinc-400 text-xs font-semibold hover:text-white transition-all">
              <Settings size={12} /> Doelen
            </button>
            <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5">
          {/* Datum selector */}
          <div className="flex items-center gap-2 mb-5">
            <input type="date" value={datum} onChange={e => setDatum(e.target.value)}
              max={new Date().toISOString().split("T")[0]}
              className="bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none flex-1" />
          </div>

          {/* Doelen instellen */}
          {showDoelen && doelForm && (
            <div className="bg-[#1a1a1a] rounded-2xl p-4 mb-4 space-y-3">
              <p className="text-white font-semibold text-sm mb-3">Macro doelen instellen voor {sporter.name}</p>
              <div className="grid grid-cols-2 gap-3">
                {MACRO_CONFIG.map(m => (
                  <div key={m.key}>
                    <label className="text-xs font-semibold mb-1 block" style={{ color: m.color }}>
                      {m.label} ({m.unit})
                    </label>
                    <input type="number" value={doelForm[m.key]} min={0}
                      onChange={e => setDoelForm(d => ({ ...d, [m.key]: Number(e.target.value) }))}
                      data-testid={`admin-doel-${m.key}`}
                      className={inputCls} />
                  </div>
                ))}
              </div>
              <button onClick={saveDoelen} disabled={savingDoelen}
                data-testid="admin-save-doelen-btn"
                className="w-full py-2.5 rounded-xl text-black font-bold text-sm disabled:opacity-40"
                style={{ background: "#C9A84C" }}>
                {savingDoelen ? "Opslaan..." : "Doelen opslaan"}
              </button>
            </div>
          )}

          {loading ? (
            <div className="space-y-3">
              {[1, 2].map(i => <div key={i} className="h-10 bg-[#1a1a1a] rounded-xl animate-pulse" />)}
            </div>
          ) : (
            <>
              {/* Macro summary */}
              <div className="bg-[#1a1a1a] rounded-2xl p-4 mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <Flame size={16} className="text-[#C9A84C]" />
                  <span className="text-white font-black text-xl tabular-nums">{Math.round(totalen.calorieen || 0)}</span>
                  <span className="text-zinc-600 text-sm">/ {Math.round(doelen.calorieen || 2000)} kcal</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {MACRO_CONFIG.slice(1).map(m => (
                    <div key={m.key}>
                      <div className="flex justify-between mb-1">
                        <span className="text-zinc-500 text-xs">{m.label}</span>
                        <span className="text-xs font-bold tabular-nums" style={{ color: m.color }}>
                          {Math.round(totalen[m.key] || 0)}g
                        </span>
                      </div>
                      <MiniProgress huidig={totalen[m.key] || 0} doel={doelen[m.key] || 0} color={m.color} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Maaltijden */}
              <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wide mb-3">
                Maaltijden ({maaltijden.length})
              </p>
              {maaltijden.length === 0 ? (
                <div className="text-center py-6 bg-[#1a1a1a] rounded-xl">
                  <Apple size={22} className="text-zinc-700 mx-auto mb-2" />
                  <p className="text-zinc-500 text-sm">Niets gelogd op deze dag</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {maaltijden.map(m => (
                    <div key={m.id} className="bg-[#1a1a1a] rounded-xl px-4 py-3">
                      <div className="flex items-center justify-between">
                        <p className="text-white text-sm font-semibold">{m.naam}</p>
                        <span className="text-[#C9A84C] text-xs font-bold">{Math.round(m.calorieen)} kcal</span>
                      </div>
                      <p className="text-zinc-600 text-xs mt-0.5">
                        {m.hoeveelheid}{m.eenheid} · {Math.round(m.eiwitten)}g eiwit · {Math.round(m.koolhydraten)}g koolh. · {Math.round(m.vetten)}g vet
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {notitie && (
                <div className="mt-3 bg-[#1a1a1a] rounded-xl p-3">
                  <p className="text-zinc-500 text-xs font-semibold mb-1">Notitie sporter</p>
                  <p className="text-zinc-300 text-sm">{notitie}</p>
                </div>
              )}
            </>
          )}
        </div>

        {toast && (
          <div className={`absolute bottom-4 left-4 right-4 flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold ${toast.type === "success" ? "bg-[#0a0a0a] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
            {toast.type === "success" ? <CheckCircle size={14} className="text-[#C9A84C]" /> : <AlertCircle size={14} className="text-red-400" />}
            {toast.msg}
          </div>
        )}
      </div>
    </div>
  );
}

export default function VoedingTab({ token }) {
  const [sporters, setSporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geselecteerd, setGeselecteerd] = useState(null);
  const [filter, setFilter] = useState("alles");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/nutrition/sporters-overzicht`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) setSporters((await r.json()).sporters || []);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "gelogd"
    ? sporters.filter(s => s.heeft_log)
    : filter === "niet_gelogd"
    ? sporters.filter(s => !s.heeft_log)
    : sporters;

  const totaalGelogd = sporters.filter(s => s.heeft_log).length;

  return (
    <div className="max-w-2xl">
      {/* Header */}
      <div className="mb-6">
        <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Gezondheid</p>
        <h2 className="text-xl font-bold text-white">Voeding</h2>
        <p className="text-zinc-500 text-sm mt-1">Voedingslog per sporter monitoren</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { val: sporters.length, label: "Sporters totaal" },
          { val: totaalGelogd, label: "Vandaag gelogd" },
          { val: sporters.length - totaalGelogd, label: "Nog niet gelogd" },
        ].map(({ val, label }) => (
          <div key={label} className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4 text-center">
            <p className="text-2xl font-black text-white tabular-nums">{val}</p>
            <p className="text-zinc-600 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-1 bg-[#0f0f0f] border border-white/5 rounded-xl p-1 w-fit mb-5">
        {[{ key: "alles", label: "Alle sporters" }, { key: "gelogd", label: "Gelogd" }, { key: "niet_gelogd", label: "Niet gelogd" }].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${filter === f.key ? "text-black" : "text-zinc-400 hover:text-white"}`}
            style={filter === f.key ? { background: "#C9A84C" } : {}}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-[#0f0f0f] border border-white/5 rounded-2xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-8 text-center">
          <Users size={28} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 font-semibold">Geen sporters</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(s => {
            const pct = s.doelen.calorieen > 0
              ? Math.min(Math.round((s.vandaag.calorieen / s.doelen.calorieen) * 100), 100)
              : 0;
            return (
              <button
                key={s.id}
                onClick={() => setGeselecteerd(s)}
                data-testid={`voeding-sporter-btn-${s.id}`}
                className="w-full bg-[#0f0f0f] border border-white/5 rounded-2xl p-4 text-left hover:border-white/10 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
                    <span className="text-[#C9A84C] font-black text-sm">{(s.name || "?")[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white font-semibold text-sm">{s.name}</p>
                      {s.heeft_log ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 font-semibold">Gelogd</span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-500 font-semibold">Niet gelogd</span>
                      )}
                    </div>
                    {s.heeft_log ? (
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <Flame size={11} className="text-[#C9A84C]" />
                          <span className="text-[#C9A84C] text-xs font-bold">{Math.round(s.vandaag.calorieen)} kcal</span>
                          {s.doelen.calorieen > 0 && (
                            <span className="text-zinc-600 text-xs">/ {s.doelen.calorieen}</span>
                          )}
                        </div>
                        <div className="flex-1 max-w-24">
                          <MiniProgress huidig={s.vandaag.calorieen} doel={s.doelen.calorieen} color="#C9A84C" />
                        </div>
                        <span className="text-zinc-600 text-xs">{pct}%</span>
                      </div>
                    ) : (
                      <p className="text-zinc-600 text-xs">Doel: {s.doelen.calorieen} kcal</p>
                    )}
                  </div>
                  <ChevronRight size={14} className="text-zinc-600 group-hover:text-zinc-400 transition-colors shrink-0" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {geselecteerd && (
        <SporterVoedingModal
          sporter={geselecteerd}
          token={token}
          onClose={() => { setGeselecteerd(null); load(); }}
        />
      )}
    </div>
  );
}
